import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { createAccountInvite } from 'api/account';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface InviteUserSettingsFormValues {
    role: string;
    email: string;
}

const InviteUserSettings = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const methods = Form.useForm<InviteUserSettingsFormValues>({
        defaultValues: {
            role: 'editor',
            email: '',
        },
    });

    const { reset, handleSubmit, control, path } = methods;

    const { isPending: isSubmitting, mutate: submit } = useMutation({
        mutationFn: (invite: InviteUserSettingsFormValues) =>
            createAccountInvite(accountId, invite),
        onSuccess: () => {
            toast.success('Invited account member successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.pendingUsers(accountId),
            });
            reset();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to invite account member');
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit(submit as SubmitHandler<InviteUserSettingsFormValues>)}
            >
                <p className="text-xl font-medium text-black">Manage Account Access</p>
                <div className="flex flex-col">
                    <p className="text-md max-w-md text-gray-600">
                        Invite new admins and editors to manage this account.{' '}
                    </p>
                    <p className="text-md max-w-md text-gray-600">
                        <a
                            target="_blank"
                            className="text-blue-500 underline"
                            rel="noopener noreferrer"
                            href="https://help.givebutter.com/en/articles/2118934-how-do-user-roles-and-permissions-work"
                        >
                            Read this guide
                        </a>{' '}
                        to learn more about managing roles.
                    </p>
                </div>
                <div className="flex flex-col">
                    <div className="flex max-w-lg flex-col gap-2 sm:flex-row">
                        <Form.Container className="flex-2">
                            <Form.Label className="sr-only" name={path('email')}>
                                Email
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={path('email')}
                                rules={{ required: 'Please enter an email' }}
                            >
                                <Form.TextInputForm placeholder="john.doe@example.com" />
                            </Form.TextInputRoot>
                        </Form.Container>
                        <Form.Container className="flex-1">
                            <Form.Label className="sr-only" name={path('role')}>
                                Role
                            </Form.Label>
                            <Form.SingleSelect control={control} name="role">
                                <SingleSelect.Trigger aria-label="Role" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="editor">
                                        <SingleSelect.ItemText>Editor</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="admin">
                                        <SingleSelect.ItemText>Admin</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </Form.Container>
                        <Form.Container className="flex-1">
                            <Form.SubmitButton
                                noAnimation={true}
                                isSubmitting={isSubmitting}
                                variant="solid"
                            >
                                Send Invite
                            </Form.SubmitButton>
                        </Form.Container>
                    </div>
                    <Form.Container>
                        <Form.Error name={path('email')} />
                    </Form.Container>
                </div>
            </form>
        </Form.Provider>
    );
};

export { InviteUserSettings };
